import { FileText, Image as ImageIcon, Loader2, Shield, Car, UtensilsCrossed, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import BriefingCard, { BriefingDetails } from "./BriefingCard";

const renderText = (msg) => {
  const parts = msg.content.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) => i % 2 === 1 ? <strong key={i} className="text-gold">{part}</strong> : part);
};

const ChatAttachment = ({ attachment }) => {
  if (!attachment) return null;
  const isImageWithUrl = attachment.file_type === "image" && attachment.file_url;
  const isError = attachment.file_type === "error";

  if (isImageWithUrl) {
    return (
      <div className="mb-2" data-testid="chat-attachment">
        <a href={attachment.file_url} target="_blank" rel="noopener noreferrer" className="block">
          <img
            src={attachment.file_url.startsWith("blob:") ? attachment.file_url : `${process.env.REACT_APP_BACKEND_URL}${attachment.file_url}`}
            alt={attachment.file_name}
            className={`max-w-full max-h-40 border border-gold/20 ${attachment.uploading ? "opacity-60" : ""}`}
          />
        </a>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mb-2" data-testid="chat-attachment">
        <div className="flex items-center gap-2 px-2 py-1.5 bg-red-500/10 border border-red-500/30 text-red-300 text-[11px] font-body">
          <FileText size={12} className="shrink-0" />
          <span className="truncate">{attachment.file_name}: {attachment.error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2" data-testid="chat-attachment">
      <div className="flex items-center gap-2 px-2 py-1.5 bg-black/40 border border-gold/20 text-white/80 text-[11px] font-body">
        {attachment.file_type === "image" ? <ImageIcon size={12} /> : <FileText size={12} />}
        <span className="truncate">{attachment.file_name}</span>
        {attachment.uploading && <Loader2 size={10} className="text-gold animate-spin shrink-0" />}
      </div>
    </div>
  );
};

/**
 * MessageBubble — renders a single chat message with all its variants:
 *   - regular user / assistant text (with bold markdown styling)
 *   - file attachment (image / document / error)
 *   - briefing card (morning or wrap-up) with KPIs + suggestions
 *   - action result block (green Shield + result string)
 *
 * All ARIA chat surfaces flow through here so styling stays consistent.
 */
export default function MessageBubble({ msg, voiceState, onSpeak, onSuggestionClick }) {
  const isUser = msg.role === "user";
  const baseClass = `max-w-[88%] px-4 py-3 text-sm font-body font-light leading-relaxed ${
    isUser
      ? "bg-gold/15 text-white"
      : msg.isBriefing
      ? "bg-gradient-to-br from-gold/10 to-white/[0.02] text-white border border-gold/15"
      : "bg-white/[0.04] text-white/80"
  }`;

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={baseClass} data-testid={msg.isBriefing ? "briefing-card" : undefined}>
        {msg.isBriefing && (
          <BriefingCard
            briefing={msg.briefing}
            content={msg.content}
            speakText={onSpeak}
            voiceEnabled={voiceState.voiceEnabled}
            setVoiceEnabled={voiceState.setVoiceEnabled}
            voiceEnabledRef={voiceState.voiceEnabledRef}
            speaking={voiceState.speaking}
            synthAvailable={voiceState.synthAvailable}
          />
        )}
        <ChatAttachment attachment={msg.attachment} />
        {renderText(msg)}
        {msg.isBriefing && <BriefingDetails briefing={msg.briefing} onSuggestionClick={onSuggestionClick} />}
        {msg.actionResult && (
          <div className="mt-2 pt-2 border-t border-gold/20 flex items-start gap-2">
            <Shield size={12} className="text-green-400 shrink-0 mt-0.5" />
            <span className="text-green-400/80 text-xs">{msg.actionResult}</span>
          </div>
        )}
        {msg.chauffeurIntent && <ChauffeurIntentCard intent={msg.chauffeurIntent} />}
      </div>
    </div>
  );
}

function ChauffeurIntentCard({ intent }) {
  const Icon = intent.type === "food" ? UtensilsCrossed : Car;
  const params = new URLSearchParams({
    type: intent.type,
    pickup: intent.pickup_address || "",
    dropoff: intent.dropoff_address || "",
    offer: intent.passenger_offer ? String(intent.passenger_offer) : "",
  });
  if (intent.pickup_lat != null && intent.pickup_lng != null) {
    params.set("p_lat", String(intent.pickup_lat));
    params.set("p_lng", String(intent.pickup_lng));
  }
  if (intent.dropoff_lat != null && intent.dropoff_lng != null) {
    params.set("d_lat", String(intent.dropoff_lat));
    params.set("d_lng", String(intent.dropoff_lng));
  }
  const fullyResolved = intent.pickup_lat != null && intent.dropoff_lat != null;
  return (
    <div data-testid="aria-chauffeur-intent" className="mt-3 p-3 border border-gold/30 bg-gold/5 rounded-md">
      <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold mb-1 flex items-center gap-1">
        <Icon size={10} /> Ride / Delivery Request
        {fullyResolved && <span className="text-green-400 text-[9px] ml-1">✓ Pinned</span>}
      </p>
      <p className="text-white text-sm font-body">
        <span className="text-gold">From:</span> {intent.pickup_display || intent.pickup_address}
      </p>
      <p className="text-white text-sm font-body">
        <span className="text-gold">To:</span> {intent.dropoff_display || intent.dropoff_address}
      </p>
      {intent.passenger_offer && (
        <p className="text-white/70 text-xs font-body mt-1">
          Your offer: <span className="text-gold font-heading">JMD {intent.passenger_offer}</span>
        </p>
      )}
      <Link to={`/chauffeur?${params.toString()}`} data-testid="aria-confirm-ride"
        className="mt-3 inline-flex items-center gap-1 bg-gold text-luxury-black px-3 py-1.5 text-xs font-body uppercase tracking-wider hover:bg-gold/90 transition-colors">
        {fullyResolved ? "Review & confirm" : "Continue to booking"} <ArrowRight size={12} />
      </Link>
    </div>
  );
}
